import { downloadFileInSameTab } from './file/downloadFileInSameTab';
import { pinReferencedCssTokens } from './snapdomCssTokenPlugin';

type SnapdomToImgOptions = {
  /**
   * output pixel density — the equivalent of html-to-image's `pixelRatio`.
   * defaults to the device pixel ratio, so captures come out retina-sharp.
   */
  dpr?: number;
  /** also save the capture as a file (via downloadFileInSameTab) */
  download?: boolean;
  fileName?: string;
};

/**
 * snapdom-based replacement for the `htmlToImg` helpers — captures the element and
 * resolves to a PNG data URL; with `download: true` it also saves the file. snapdom
 * clones + inlines styles in one pass instead of html-to-image's per-node walk, so the
 * main thread stays responsive on large subtrees.
 *
 * Output resolution follows snapdom's default `dpr` (the screen's devicePixelRatio),
 * so captures come out retina-sharp and previews match what html-to-image produced.
 */
export async function snapdomToImg(
  el: HTMLElement,
  options?: SnapdomToImgOptions,
): Promise<string> {
  try {
    const { snapdom } = await import('@zumer/snapdom');

    const capture = await snapdom(el, {
      // output size = element size × scale × dpr; `scale` stays at its default (1), so
      // dpr alone decides density — same contract as html-to-image's `pixelRatio`.
      dpr: options?.dpr ?? (window.devicePixelRatio || 1),
      // the snapshot renders inside an isolated SVG <img>, so any app font not inlined here
      // falls back to a system font and the capture stops matching what's on screen.
      embedFonts: true,
      plugins: [pinReferencedCssTokens],
    });

    // going through a canvas (rather than toBlob) keeps the data-URL output that
    // downloadFileInSameTab needs for the flutter/react-native webview branch.
    const canvas = await capture.toCanvas();
    const dataUrl = canvas.toDataURL('image/png');

    if (!dataUrl) throw new Error('Failed to capture image');

    if (options?.download) {
      downloadFileInSameTab(dataUrl, options.fileName ?? 'captured-image.png');
    }

    return dataUrl;
  } catch (error) {
    console.error(error);
    throw error;
  }
}
